import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const STATUS_VARIANT = { OPEN: 'warning', POSTED: 'success', CANCELLED: 'neutral' };

export function CycleCountDetailModal({ countId, onClose, warehouse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['inventory', 'cycle-count', countId],
    queryFn: () => inventoryApi.getCycleCount(countId).then((r) => r.items),
    enabled: Boolean(countId),
  });

  const post = useMutation({
    mutationFn: () => inventoryApi.postCycleCount(countId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'cycle-counts'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'cycle-count', countId] });
      onClose();
    },
  });

  const cc = detail.data;
  const lines = cc?.lines || [];
  const isOpen = cc?.status === 'OPEN';
  const adjustments = lines.filter((l) => Number(l.variance) !== 0).length;

  return (
    <Modal
      open={Boolean(countId)}
      onClose={onClose}
      title={t('cyc.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {isOpen && (
            <Button variant="primary" onClick={() => post.mutate()} disabled={post.isPending}>
              {post.isPending ? `${t('cyc.action.post')}…` : t('cyc.action.post')}
            </Button>
          )}
        </>
      }
    >
      {cc && (
        <p className="muted text-sm">
          {[warehouse?.code, <Badge key="s" variant={STATUS_VARIANT[cc.status]}>{t(`cyc.status.${cc.status}`)}</Badge>]
            .filter(Boolean)
            .reduce((acc, x, i) => (i === 0 ? [x] : [...acc, ' · ', x]), [])}
        </p>
      )}
      {post.isError && <div className="alert danger">{post.error?.message || t('common.loadError')}</div>}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t('cyc.field.product')}</th>
              <th className="num">{t('cyc.field.expected')}</th>
              <th className="num">{t('cyc.field.counted')}</th>
              <th className="num">{t('cyc.field.variance')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const v = Number(l.variance);
              return (
                <tr key={l.id}>
                  <td>{l.sku} · {l.productName}</td>
                  <td className="num">{l.expectedQty}</td>
                  <td className="num">{l.countedQty}</td>
                  <td className="num" style={{ color: v < 0 ? 'var(--color-danger)' : v > 0 ? 'var(--color-success)' : 'inherit' }}>
                    {v > 0 ? `+${l.variance}` : l.variance}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isOpen && <p className="muted text-sm">{t('cyc.detail.willAdjust', { count: String(adjustments) })}</p>}
    </Modal>
  );
}
